import React from 'react';
import {
  Users,
  Wallet,
  BarChart2,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StatCard, PageHeader } from './ui';
import AumPreviewCard from './dashboard/AumPreviewCard';
import CashMovementChart from './dashboard/CashMovementChart';
import EmptyChartOverlay from './dashboard/EmptyChartOverlay';
import GettingStartedBanner from './dashboard/GettingStartedBanner';
import OrdersActivityChart from './dashboard/OrdersActivityChart';
import OrdersBreakdownCard from './dashboard/OrdersBreakdownCard';
import { pickCashCurrency, useCashMovementData } from './dashboard/useCashMovementData';
import { useOrdersActivityData } from './dashboard/useOrdersActivityData';
import { DASHBOARD_DISPLAY_CURRENCY, MAX_PORTFOLIO_FANOUT, useOrgPortfolioTotals } from './dashboard/useOrgPortfolioTotals';
import { formatMoney } from './dashboard/formatters';

const KPI_ICONS = {
  aum: Wallet,
  'funded-customers': Users,
  'orders-mtd': BarChart2,
  'trading-volume': TrendingUp,
};

const formatKpiValue = (kpi) => {
  if (kpi.loading) return '—';
  switch (kpi.valueType) {
    case 'money':
      return formatMoney(kpi.value, kpi.currency, { compact: true });
    case 'count':
      return kpi.value.toLocaleString('en-US');
    default:
      return String(kpi.value);
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  // AUM, market value and investors are summed from the same per-customer
  // portfolios the Portfolio page shows, in the same display currency.
  const { loading: orgSummaryLoading, totals } = useOrgPortfolioTotals(DASHBOARD_DISPLAY_CURRENCY);

  const { loading: cashLoading, deposits, withdrawals } = useCashMovementData();
  const { loading: ordersLoading, orders } = useOrdersActivityData();

  // Label and figures in the currency the approved money is actually in.
  const cashCurrency = pickCashCurrency(deposits, withdrawals, DASHBOARD_DISPLAY_CURRENCY);
  const firstName = user?.name?.split(' ')[0] || '';

  const aumCurrency = totals?.currency || DASHBOARD_DISPLAY_CURRENCY;
  const totalAum = totals?.totalPortfolioValue ?? 0;
  const totalMarketValue = totals?.totalMarketValue ?? 0;
  const totalCustomers = totals?.totalCustomers ?? 0;
  let portfolioCaption = 'Live from portfolio service';
  if (!totals || totals.failed) {
    portfolioCaption = 'Portfolio data unavailable';
  } else if (totals.customersLoaded > MAX_PORTFOLIO_FANOUT) {
    portfolioCaption = `First ${MAX_PORTFOLIO_FANOUT} of ${totals.customersLoaded} customers`;
  } else if (totals.unpricedCustomers > 0) {
    portfolioCaption = `${totals.unpricedCustomers} portfolio${totals.unpricedCustomers === 1 ? '' : 's'} unavailable`;
  }

  const dataReady = !orgSummaryLoading && !ordersLoading && !cashLoading;
  const isFreshAccount = dataReady && totalCustomers === 0 && orders.length === 0 && deposits.length === 0;
  const orders24h = ordersLoading
    ? 0
    : orders.filter((o) => new Date(o.createdAt).getTime() >= Date.now() - 86400000).length;

  // No period-over-period comparison data is available yet (would require a
  // historical snapshot), so these KPIs intentionally render without a
  // delta/trend badge rather than a fabricated "+0.0%".
  const heroKpis = [
    {
      key: 'aum',
      label: 'Assets under management',
      value: totalAum,
      valueType: 'money',
      currency: aumCurrency,
      deltaLabel: portfolioCaption,
      loading: orgSummaryLoading,
    },
    {
      key: 'funded-customers',
      label: 'Total customers',
      value: totalCustomers,
      valueType: 'count',
      deltaLabel: 'Live from portfolio service',
      loading: orgSummaryLoading,
    },
    {
      key: 'orders-mtd',
      label: 'Orders (last 24h)',
      value: orders24h,
      valueType: 'count',
      deltaLabel: 'Live from orders service',
      loading: ordersLoading,
    },
    {
      key: 'trading-volume',
      label: 'Market value (equities)',
      value: totalMarketValue,
      valueType: 'money',
      currency: aumCurrency,
      deltaLabel: portfolioCaption,
      loading: orgSummaryLoading,
    },
  ];

  return (
    <div>
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : 'Overview'}
      />

      <GettingStartedBanner
        totalCustomers={totalCustomers}
        customersLoading={orgSummaryLoading}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {heroKpis.map((kpi) => {
          const Icon = KPI_ICONS[kpi.key];
          return (
            <StatCard
              key={kpi.key}
              label={kpi.label}
              value={formatKpiValue(kpi)}
              deltaLabel={kpi.loading ? null : kpi.deltaLabel}
              icon={Icon}
            />
          );
        })}
      </div>

      <div className="mt-6">
        <AumPreviewCard loading={orgSummaryLoading} totals={totals} />
      </div>

      <div className="mt-6 relative">
        <CashMovementChart loading={cashLoading} deposits={deposits} withdrawals={withdrawals} baseCurrency={cashCurrency} />
        {isFreshAccount && !cashLoading && (
          <EmptyChartOverlay
            message="Cash movement data will appear once customers start making deposits and withdrawals."
            cta="Add a customer"
            ctaHref="/customers"
          />
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 relative">
          <OrdersActivityChart orders={orders} loading={ordersLoading} />
          {isFreshAccount && !ordersLoading && (
            <EmptyChartOverlay
              message="Order activity will appear once your customers place their first trades."
              cta="View customers"
              ctaHref="/customers"
            />
          )}
        </div>
        <div>
          <OrdersBreakdownCard orders={orders} loading={ordersLoading} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
